import {computed, observable, React, _} from '@api'
import {AssortmentHeroCardStore, HeroCardItemStore, HeroCardSecondLevelStore, SpecificationItemStore} from '@design-board'
import {AssortmentOrientationType, DEBUG_PDF, IAssortmentBoardTemplate, iRhRenderStore} from '@modules/pdf'
import styled from '@react-pdf/styled-components'
import {View, Text} from '@react-pdf/renderer'
import {NoImageRH, NoImageRH_Original} from '@resources'
import {StoresInstance, useCatalog} from '@stores'

interface PageSize {
  width: number
  height: number
}

export const RHStylesContext = React.createContext<RHStyles>(null)
export const useRHStyles = () => React.useContext(RHStylesContext)

export class RHStyles {
  constructor(public stores: StoresInstance, public props: IAssortmentBoardTemplate) {}

  @computed get download() {
    return this.props.download
  }

  @observable pageSize: PageSize = {width: 302.4 * this.multiplier, height: 648 * this.multiplier}

  get headerHeight() {
    return 230 * this.multiplier
  }

  @computed get headerSize() {
    const {heroTableWidth, headerHeight: height} = this
    return {
      width: heroTableWidth,
      height
    }
  }

  get horizontalInset() {
    return 18 * this.multiplier
  }

  get verticalInset() {
    return 19 * this.multiplier
  }

  get spaceBetweenHeroCards() {
    return 10.9 * this.multiplier
  }

  get titleHeight() {
    return 34 * this.multiplier
  }

  @computed get heroTableWidth() {
    const {pageSize: {width}, horizontalInset} = this
    return width - (horizontalInset * 2)
  }

  @computed get heroCardImageContainerHeight() {
    const {pageSize: {height}, headerSize: {height: headerHeight}, multiplier, swatchSize: {height: swatchHeight}, verticalInset, spaceBetweenHeroCards} = this
    return height - headerHeight - (swatchHeight + (2.5 * multiplier)) - (5 * spaceBetweenHeroCards) - (verticalInset * 2)
  }

  @computed get heroCardFirstRowHeight() {
    const {headerSize: {height}, titleHeight, spaceBetweenHeroCards, verticalInset} = this
    return height - titleHeight - (verticalInset * 2)
  }

  get heroCardFirstTopMargin() {
    return 8 * this.multiplier
  }

  @computed get heroCardFirstContainerHeight() {
    const {heroCardFirstRowHeight, heroCardFirstTopMargin} = this
    return heroCardFirstRowHeight - (heroCardFirstTopMargin)
  }

  get heroCardTopMargin() {
    return 11 * this.multiplier
  }

  @computed get heroCardContainerHeight() {
    const {heroCardRowHeight, heroCardTopMargin} = this
    return heroCardRowHeight - heroCardTopMargin
  }

  @computed get heroCardRowHeight() {
    const {heroCardImageContainerHeight, spaceBetweenHeroCards, multiplier} = this
    return Math.ceil(((heroCardImageContainerHeight + (spaceBetweenHeroCards - (2.5 * multiplier))) / 3))
  }

  @computed get heroCardImageWidth() {
    const {heroTableWidth, spaceBetweenHeroCards} = this
    return Math.floor((heroTableWidth - spaceBetweenHeroCards) / 2)
  }

  @computed get swatchHeight() {
    const {pageSize: {width}, horizontalInset, spaceBetweenHeroCards} = this
    return ((width - (horizontalInset * 2)) / 6) - spaceBetweenHeroCards
  }

  @computed get swatchSize() {
    const {swatchHeight: height} = this
    return {
      width: height,
      height
    }
  }

  @computed get swatchImageSize() {
    const {swatchHeight: size} = this
    return {
      size
    }
  }

  get font() {
    return 'Baron Sans Thin'
  }

  get fontSizes() {
    const {multiplier} = this
    return {
      20: `${2 * multiplier}px`,
      29: `${2.9 * multiplier}px`,
      33: `${3.3 * multiplier}px`,
      40: `${4 * multiplier}px`,
      60: `${6 * multiplier}px`,
      77: `${7.7 * multiplier}px`,
      87: `${8.7 * multiplier}px`,
      122: `${12.2 * multiplier}px`,
      250: `${25 * multiplier}px`
    }
  }

  @computed get multiplier() {
    return this.download ? 10 : 5
  }

  @computed get noImageUrl() {
    const {stores: {catalog: {imageBackgroundUrl}}} = this
    return this.download ? imageBackgroundUrl : NoImageRH
  }

}

export const RestorationHardwareStyle = (rhStyles: RHStyles) => {
  const {multiplier} = rhStyles
  return {
    pageSize: rhStyles.pageSize,
    RootView: styled.View`
      margin: ${rhStyles.verticalInset}px ${rhStyles.horizontalInset}px;
    `,
    HeaderView: styled.View``,
    Title: styled.Text`
      font-family: ${rhStyles.font};
      font-size: ${rhStyles.fontSizes['122']};
      text-transform: uppercase;
      margin-bottom: ${rhStyles.spaceBetweenHeroCards + (3 * multiplier)}px;
      letter-spacing: ${1.3 * multiplier}px;
      padding-top: ${0.5 * multiplier}px;
      color: #131415;
    `,
    SubTitleLabel: styled.Text`
      font-family: ${rhStyles.font};
      font-size: ${rhStyles.fontSizes['77']};
      width: ${52 * multiplier}px;
      text-transform: uppercase;
      display: flex;
      justify-content: space-between;
      margin-right: ${0.8 * multiplier}px;
      letter-spacing: ${0.9 * multiplier}px;
    `,
    SubTitleContent: styled.Text`
      font-family: ${rhStyles.font};
      font-size: ${rhStyles.fontSizes['77']};
      height: ${9.5 * multiplier}px;
      text-transform: uppercase;
      display: flex;
      justify-content: space-between;
      letter-spacing: ${0.9 * multiplier}px;
    `,
    HeaderImageView: styled.View`
      display: flex;
      flex-direction: row;
      margin: ${((rhStyles.spaceBetweenHeroCards - (3.5 * multiplier)) / 2)}px 0;
      align-items: center;
      width: 100%;
      height: ${20 * multiplier}px;
    `,
    ProfileImage: styled.Image`
      width: ${45 * multiplier}px;
      height: ${45 * multiplier}px;;
      object-fit: contain;
    `,
    NoImageView: styled.View`
      width: ${45 * multiplier}px;
      height: ${45 * multiplier}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #EAEAEA;
      background-color: #EAEAEA;
    `,
    NoImageText: styled.Text`
      font-size: ${rhStyles.fontSizes['250']};
      color: #838383;
      text-transform: uppercase;
    `,
    SubTitleDetailView: styled.View`
      display: flex;
      flex-direction: row;
      align-items: center;
    `,
    SubTitleView: styled.View`
      margin-left: ${5 * multiplier}px;
    `,
    FirstViewImage: styled.Image`
      width: 100%;
      height: ${rhStyles.heroCardFirstRowHeight}px;
      object-fit: cover;
    `,
    Images: styled.View`
      margin-top: ${rhStyles.spaceBetweenHeroCards}px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      height: ${rhStyles.heroCardImageContainerHeight}px;
    `,
    ImagesViewContainer: styled.View`
      display: flex;
      justify-content: space-between;
      width: ${rhStyles.heroTableWidth + rhStyles.spaceBetweenHeroCards}px;
      height: ${rhStyles.heroCardRowHeight}px;
      flex-direction: row;
    `,
    MainImageView: styled.View`
      position: relative;
    `,
    MainImage: styled.Image`
      object-fit: cover;
      position: relative;
      width: 100%;
      height: 100%;
    `,
    ProductImage: styled.Image`
      height: ${p => p.fullWidth ? ((rhStyles.heroCardFirstContainerHeight / ((p.orientation === 'vertical') ? 2 : 1)) - ((p.wrapedSecondLevel ? 35 : 30) * rhStyles.multiplier))
                                 : ((rhStyles.heroCardContainerHeight / ((p.orientation === 'vertical') ? 2 : 1)) - ((p.wrapedSecondLevel ? 35 : 30) * rhStyles.multiplier))}px;
      display: flex;
      align-items: flex-start;
      align-self: flex-start;
      justify-content: flex-start;
      object-fit: contain;
    `,
    Swatches: styled.View`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-top: ${rhStyles.spaceBetweenHeroCards}px;
      width: ${(rhStyles.heroTableWidth)}px;
    `,
    SwatchView: styled.View`
      display: flex;
      align-items: center;
      height: ${p => (rhStyles.swatchSize.height + ((p.hasImage ? 4 : 0) * multiplier))}px;
      width: ${rhStyles.swatchSize.width}px;
    `,
    SwatchImage: styled.Image`
      min-width: ${rhStyles.swatchImageSize.size}px;
      min-height: ${rhStyles.swatchImageSize.size}px;
      object-fit: contain;
    `,
    SwatchText: styled.Text`
      font-size: ${rhStyles.fontSizes['33']};
      font-family: ${rhStyles.font};
      margin-top: ${2 * multiplier}px;
      text-transform: uppercase;
      text-align: center;
      width: 100%;
    `,
    ImageTitleView: styled.View`
      position: absolute;
      width: ${p => p.fullWidth ? (rhStyles.heroTableWidth) : (rhStyles.heroCardImageWidth - ((p.subdivide && p.orientation === 'horizontal') ? ((rhStyles.heroCardImageWidth / 2) + (rhStyles.spaceBetweenHeroCards / 2)) : 0))}px;
      display: flex;
      align-items: center;
      justify-content: center;
      top: ${props => (props.fullWidth ? 5 : 5) * multiplier}px;
    `,
    ImageTitleText: styled.Text`
      font-family: ${rhStyles.font};
      font-size: ${rhStyles.fontSizes['40']};
      text-transform: uppercase;
    `,
    MainImageContainer: styled.View`
      object-fit: ${props => props.hasImage ? 'contain' : 'cover'};
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      border: 0;
      position: relative;
      height: ${p => (p.fullWidth ? rhStyles.heroCardFirstRowHeight : rhStyles.heroCardRowHeight) / ((p.subdivide && p.orientation === 'vertical') ? 2 : 1)}px;
      width: ${p => p.fullWidth ? rhStyles.heroTableWidth : rhStyles.heroCardImageWidth}px;
      margin-top: ${rhStyles.spaceBetweenHeroCards}px;
      margin-left: ${p => ((p.index % 2) === 0 && !p.fullWidth) ? p.orientation === 'vertical' ? 0 : (rhStyles.spaceBetweenHeroCards - 1) : 0}px;
    `,
    CombineImageContainer: styled.View`
      display: flex;
      border: 0;
      position: relative;
      height: ${p => p.fullWidth ? rhStyles.heroCardFirstRowHeight : (rhStyles.heroCardRowHeight + (p.subdivide ? rhStyles.spaceBetweenHeroCards : 0))}px;
      flex-grow: 1;
    `,
    SecondLevelViewContainer: styled.View`
      display: flex;
      width: 100%;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-end;
      justify-content: center;
      position: relative;
    `,
    SecondLevelIndividualContainer: styled.View`
      display: flex;
      align-items: center;
      justify-content: flex-start;
        //height: ${23 * rhStyles.multiplier}px;
      margin: 0;
    `,
    SecondLevelImage: styled.Image`
      height: ${12 * rhStyles.multiplier}px;
      object-fit: contain;
    `,
    SecondLevelText: styled.Text`
      font-size: ${p => rhStyles.fontSizes[p.combine ? '20' : '29']};
      font-family: ${rhStyles.font};
      margin-top: ${p => (p.combine ? 0.8 : 2) * multiplier}px;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: center;
      width: 100%;
      flex: 1
    `,
    CombineItemImageWrapper: styled.View`
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: flex-start;
      position: relative;
      flex: 1;
    `,
    CombineImageWrapper: styled.View`
      display: flex;
        // top: ${p => p.fullWidth ? rhStyles.heroCardFirstTopMargin : (p.subdivide && p.orientation === 'vertical') ? 100 : rhStyles.heroCardTopMargin}px;
      bottom: 0;
      position: absolute;
      align-items: center;
      flex-direction: ${p => p.orientation === 'vertical' ? 'column' : 'row'};
      width: ${p => p.fullWidth ? (rhStyles.heroTableWidth) : (rhStyles.heroCardImageWidth - ((p.subdivide && p.orientation === 'horizontal') ? ((rhStyles.heroCardImageWidth / 2) + (rhStyles.spaceBetweenHeroCards / 2)) : 0))}px;
      height: ${p => p.fullWidth ? (rhStyles.heroCardFirstContainerHeight - (rhStyles.multiplier * 2))
                                 : (rhStyles.heroCardContainerHeight / ((p.subdivide && p.orientation === 'vertical') ? 2.75 : 1))}px;
        //margin-left: ${props => ((props.index % 2) === 1 || props.fullWidth || (props.subdivide)) ? 0 : rhStyles.spaceBetweenHeroCards}px;
    `
  }
}

export const ImageText = ({text, fullWidth, orientation, subdivide}) => {
  const store = useRHStyles(),
    {ImageTitleView, ImageTitleText} = RestorationHardwareStyle(store)
  return (
    <ImageTitleView fullWidth={fullWidth} orientation={orientation} subdivide={subdivide}>
      <ImageTitleText>{_.toUpper(text)}</ImageTitleText>
    </ImageTitleView>
  )
}

interface iAssortmentSecondLevelViewProps {
  store: Array<HeroCardSecondLevelStore>,
  fullWidth: boolean
  index: number
  combine?: boolean
  orientation?: AssortmentOrientationType
}

const AssortmentSecondLevelView = (props: iAssortmentSecondLevelViewProps) => {
  const rhStore = useRHStyles(),
    {store, fullWidth, index, orientation} = props,
    {SecondLevelViewContainer, SecondLevelIndividualContainer, SecondLevelImage, SecondLevelText} = RestorationHardwareStyle(rhStore)

  return (
    <SecondLevelViewContainer fullWidth={fullWidth} index={index}>
      {store.map((f, i) => {
        const {imageUrl, attributes, id, config: {child: {removeDecimalsInAttributes}}} = f
        return (
          <SecondLevelIndividualContainer fullWidth={fullWidth} key={`${id}_${i}`}>
            {imageUrl && <SecondLevelImage orientation={orientation} src={imageUrl}/>}
            {attributes.map((attrVal: any, n: number) => {
              if (attrVal) {
                let val = attrVal
                if (removeDecimalsInAttributes) {
                  val = val.replaceAll(/\.[0-9]+/g, '')
                }
                return <SecondLevelText key={`${val}_${i}_${n}`}>{val}</SecondLevelText>
              }
              return null
            }).filter((a: any) => a)}
          </SecondLevelIndividualContainer>
        )
      }).filter((f: any) => f)}
    </SecondLevelViewContainer>
  )
}

interface iImageView {
  store: iRhRenderStore,
  fullWidth?: boolean
  showBackground?: boolean
  showTitle?: boolean
  combine?: boolean
  index?: number
  subdivide?: boolean
  orientation?: AssortmentOrientationType
}

export const AssortmentImageView = (props: iImageView) => {
  const rhStore = useRHStyles(),
    {MainImageContainer, CombineImageWrapper, CombineItemImageWrapper, CombineImageContainer} = RestorationHardwareStyle(rhStore),
    {store, fullWidth, combine, index} = props,
    {assortmentHeroCardStore: hero, heroCardItemStore: item, footerStores: footer, combineStores} = store,
    {props: {index: i}, titleStore, cardStore: {overrides}} = hero,
    {noImageUrl} = rhStore,
    imageUrl = item.imageUrls[0] ?? item.placeHolderUrl ?? noImageUrl,
    hasImage = imageUrl !== noImageUrl && !!imageUrl,
    subdivide = props.subdivide ?? overrides?.subdivide?.[titleStore.title]

  let combineItem: HeroCardItemStore = null, combineFooter: Array<HeroCardSecondLevelStore> = null, orientation: AssortmentOrientationType = props.orientation ?? null, combineImageUrl, combineHasImage
  if (combine) {
    const combineStore = combineStores[0]
    combineItem = combineStore.heroCardItemStore
    combineFooter = combineStore.footerStores
    orientation = combineStore.orientation
    combineImageUrl = combineItem.imageUrls[0] ?? combineItem.placeHolderUrl ?? noImageUrl
    combineHasImage = combineImageUrl !== noImageUrl && combineImageUrl !== ''
    DEBUG_PDF && console.log({orientation})
  }

  if (subdivide && combine) {
    const left = (index % 2) === 0,
      right = (index % 2) === 1,
      horizontal = orientation === 'horizontal',
      maxWidth = (rhStore.heroCardImageWidth + ((left && orientation === 'horizontal') ? (rhStore.spaceBetweenHeroCards - 1) : 0))
    return (
      <CombineImageContainer index={index} fullWidth={fullWidth} subdivide={subdivide} orientation={orientation} style={{flexDirection: horizontal ? 'row' : 'column', maxWidth}}>
        <AssortmentImageView store={store} index={index} combine={false} subdivide={subdivide} orientation={orientation}/>
        <AssortmentImageView store={combineStores[0]} index={index + (right && horizontal ? 1 : 0)} combine={false} subdivide={subdivide} orientation={orientation}/>
      </CombineImageContainer>
    )
  }

  return (
    <MainImageContainer fullWidth={fullWidth} index={index}>
      <ItemImage secondLevelStore={footer} hasImage={false} index={index} imageUrl={noImageUrl} fullWidth={fullWidth} orientation={orientation} subdivide={subdivide}/>
      <ImageText text={titleStore.title} fullWidth={fullWidth} orientation={orientation} subdivide={subdivide}/>
      <CombineImageWrapper subdivide={subdivide} index={index} orientation={orientation} fullWidth={fullWidth}>
        <CombineItemImageWrapper subdivide={subdivide} orientation={orientation}>
          <ItemImage secondLevelStore={footer} combine={orientation === 'vertical' && combine} orientation={orientation} showBackground={false} hasImage={hasImage} index={index} imageUrl={imageUrl} fullWidth={fullWidth}/>
          <AssortmentSecondLevelView combine orientation={orientation} store={footer} fullWidth={fullWidth} index={index}/>
        </CombineItemImageWrapper>
        {combine === true && <CombineItemImageWrapper>
			<ItemImage secondLevelStore={footer} combine={orientation === 'vertical'} orientation={orientation} showBackground={false} hasImage={combineHasImage} index={index} imageUrl={combineImageUrl} fullWidth={fullWidth}/>
			<AssortmentSecondLevelView combine store={combineFooter} orientation={orientation} fullWidth={fullWidth} index={index}/>
		</CombineItemImageWrapper>}
      </CombineImageWrapper>
    </MainImageContainer>
  )
}

export const ItemImage = (props) => {
  const store = useRHStyles(),
    {fullWidth, orientation, subdivide, imageUrl, index, hasImage, showBackground, combine, secondLevelStore} = props,
    {MainImageView, MainImage, ProductImage} = RestorationHardwareStyle(store)

  return (
    <MainImageView>
      {showBackground !== false && <MainImage subdivide={subdivide} index={index} src={store.noImageUrl} hasImage={false} fullWidth={fullWidth} orientation={orientation}/>}
      {hasImage && <ProductImage subdivide={subdivide} combine={combine} index={index} fullWidth={fullWidth} wrapedSecondLevel={secondLevelStore.length > (fullWidth ? 10 : 5)} src={imageUrl} hasImage={hasImage} width={0} height={0} orientation={orientation}/>}
    </MainImageView>
  )

}

export const AssortmentDetails = (props) => {
  const store = useRHStyles(),
    {stores: {strings}} = store,
    {attributes, profileImageUrl} = props,
    {ProfileImage, SubTitleLabel, SubTitleContent, HeaderImageView, SubTitleDetailView, NoImageText, NoImageView, SubTitleView} = RestorationHardwareStyle(store)

  return (
    <HeaderImageView>
      <ProfileImage src={profileImageUrl ?? store.noImageUrl}/>
      <SubTitleView>
        {attributes.map(({field, label, value}, i) =>
          <SubTitleDetailView key={i}>
            <SubTitleLabel>{label}:</SubTitleLabel>
            <SubTitleContent>{(_.isArray(value) ? value.join(', ') : value === strings.noValue ? '' : value) || ''}</SubTitleContent>
          </SubTitleDetailView>)}
      </SubTitleView>
    </HeaderImageView>
  )
}

export const SwatchesView = (props) => {
  const store = useRHStyles(),
    {data} = props,
    {Swatches, SwatchImage, SwatchText, SwatchView} = RestorationHardwareStyle(store)
  return (
    <Swatches>
      {data.map((swatch, i) => {
        const imageUrl = !!!swatch.imageUrl ? swatch.imageBackground ?? NoImageRH
                                            : swatch.imageUrl
        return (
          <SwatchView hasImage={!!swatch.imageUrl} key={swatch.exId ?? swatch.id ?? i}>
            <SwatchImage hasImage={!!swatch.imageUrl} src={imageUrl}/>
            <SwatchText>{swatch.title ?? swatch.name ?? ''}</SwatchText>
          </SwatchView>
        )
      })}
    </Swatches>
  )
}


// WEBPACK FOOTER //
// ./src/modules/pdf/templates/restoration-hardware/restoration-hardware.style.tsx